import axios from "axios";

const toHttpsIfNeeded = (url) => {
    if (!url || typeof url !== 'string') return url;
    if (typeof window !== 'undefined' && window.location?.protocol === 'https:' && url.startsWith('http://')) {
        return `https://${url.slice('http://'.length)}`;
    }
    return url;
};

const normalizeBaseURL = (url) => {
    if (!url || typeof url !== 'string') return url;
    return url.trim().replace(/\/+$/, '');
};

const baseURL = toHttpsIfNeeded(normalizeBaseURL(process.env.REACT_APP_API_URL));


// GET REQUESTS

const eventAuth = () => {
    const token = localStorage.getItem('token');
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

export const getEventsPage = async (params = {}) => {
    const { data } = await axios.get(`${baseURL}/events/`, { ...eventAuth(), params });
    // Keep compatibility with non-paginated responses during deployment.
    if (Array.isArray(data)) {
        return { results: data, count: data.length, next: null, previous: null };
    }
    return { count: data.results?.length || 0, next: null, previous: null, ...data };
};

export const getEventsInRange = async (start, end) => {
    const events = [];
    let page = 1;
    let result;
    do {
        result = await getEventsPage({ start, end, page });
        events.push(...result.results);
        page += 1;
    } while (result.next);
    return events;
};

export const getEvent = async (id) => {
    const { data } = await axios.get(`${baseURL}/events/${id}/`, eventAuth());
    return data;
};

// MEMBERS

export const getMemberMe = async () => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Not authenticated');

    const url = `${baseURL}/members/me/`;
    const config = { headers: { Authorization: `Bearer ${token}` } };
    const res = await axios.get(url, config);
    return res.data;
};

export const patchMemberMe = async (payload) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Not authenticated');

    const url = `${baseURL}/members/me/`;
    const config = { headers: { Authorization: `Bearer ${token}` } };
    const res = await axios.patch(url, payload, config);
    return res.data;
};

export const searchMemberDirectory = async (q) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Not authenticated');

    const url = `${baseURL}/members/directory/`;
    const config = {
        headers: { Authorization: `Bearer ${token}` },
        params: q ? { q } : {},
    };
    const res = await axios.get(url, config);
    return Array.isArray(res.data) ? res.data : [];
};

export const getUpcomingBirthdays = async (days = 30) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Not authenticated');

    const url = `${baseURL}/members/birthdays/`;
    const config = {
        headers: { Authorization: `Bearer ${token}` },
        params: { days },
    };
    const res = await axios.get(url, config);
    return Array.isArray(res.data) ? res.data : [];
};

// WORK HOURS

export const createWorkHour = async (payload) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Not authenticated');

    const url = `${baseURL}/workhours/`;
    const config = { headers: { Authorization: `Bearer ${token}` } };
    const res = await axios.post(url, payload, config);
    return res.data;
};

export const getMyWorkHours = async () => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Not authenticated');

    const url = `${baseURL}/workhours/my/`;
    const config = { headers: { Authorization: `Bearer ${token}` } };
    const res = await axios.get(url, config);

    // Support paginated & non-paginated
    if (Array.isArray(res.data)) return res.data;
    if (res.data && Array.isArray(res.data.results)) return res.data.results;
    return [];
};

export const getAdminWorkHours = async ({ q, approved } = {}) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Not authenticated');

    const url = `${baseURL}/workhours/`;
    const params = {};
    if (typeof q === 'string' && q.trim()) params.q = q.trim();
    if (approved === true) params.approved = true;
    if (approved === false) params.approved = false;

    const config = { headers: { Authorization: `Bearer ${token}` }, params };
    const res = await axios.get(url, config);

    if (Array.isArray(res.data)) return res.data;
    if (res.data && Array.isArray(res.data.results)) return res.data.results;
    return [];
};

export const approveWorkHour = async (id) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Not authenticated');

    const url = `${baseURL}/workhours/${id}/approve/`;
    const config = { headers: { Authorization: `Bearer ${token}` } };
    const res = await axios.post(url, {}, config);
    return res.data;
};

export const getWorkHoursAnalysis = async ({ year, onlyZero } = {}) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Not authenticated');

    const url = `${baseURL}/workhours/analysis/`;
    const params = {};
    if (Number.isInteger(year)) params.year = year;
    if (onlyZero === true) params.only_zero = true;

    const config = { headers: { Authorization: `Bearer ${token}` }, params };
    const res = await axios.get(url, config);

    return Array.isArray(res.data) ? res.data : [];
};

// ASSETS / ASSET BOOKINGS

export const getAssets = async ({ active } = {}) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Not authenticated');

    const url = `${baseURL}/assets/`;
    const params = {};
    if (typeof active === 'boolean') params.active = active;
    const config = { headers: { Authorization: `Bearer ${token}` }, params };
    const items = [];
    let nextUrl = url;
    let nextConfig = config;

    while (nextUrl) {
        const res = await axios.get(nextUrl, nextConfig);

        if (Array.isArray(res.data)) return res.data;
        if (res.data && Array.isArray(res.data.results)) {
            items.push(...res.data.results);
            nextUrl = res.data.next;
            nextConfig = { headers: config.headers };
        } else {
            nextUrl = null;
        }
    }

    if (items.length) return items;
    return [];
};

export const createAssetBooking = async (payload) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Not authenticated');

    const url = `${baseURL}/asset-bookings/`;
    const config = { headers: { Authorization: `Bearer ${token}` } };
    const res = await axios.post(url, payload, config);
    return res.data;
};

export const getMyAssetBookings = async () => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Not authenticated');

    const url = `${baseURL}/asset-bookings/my/`;
    const config = { headers: { Authorization: `Bearer ${token}` } };
    const res = await axios.get(url, config);

    if (Array.isArray(res.data)) return res.data;
    if (res.data && Array.isArray(res.data.results)) return res.data.results;
    return [];
};

export const getAssetBookingCalendar = async ({ start, end } = {}) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Not authenticated');

    const url = `${baseURL}/asset-bookings/calendar/`;
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    const config = { headers: { Authorization: `Bearer ${token}` }, params };
    const res = await axios.get(url, config);

    if (Array.isArray(res.data)) return res.data;
    if (res.data && Array.isArray(res.data.results)) return res.data.results;
    return [];
};

export const getAdminAssetBookings = async ({ q, status, asset, start, end } = {}) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Not authenticated');

    const url = `${baseURL}/asset-bookings/`;
    const params = {};
    if (typeof q === 'string' && q.trim()) params.q = q.trim();
    if (typeof status === 'string' && status.trim()) params.status = status.trim();
    if (asset) params.asset = asset;
    if (start) params.start = start;
    if (end) params.end = end;

    const config = { headers: { Authorization: `Bearer ${token}` }, params };
    const items = [];
    let nextUrl = url;
    let nextConfig = config;

    while (nextUrl) {
        const res = await axios.get(nextUrl, nextConfig);

        if (Array.isArray(res.data)) return res.data;
        if (res.data && Array.isArray(res.data.results)) {
            items.push(...res.data.results);
            nextUrl = res.data.next;
            nextConfig = { headers: config.headers };
        } else {
            nextUrl = null;
        }
    }

    if (items.length) return items;
    return [];
};

export const approveAssetBooking = async (id) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Not authenticated');

    const url = `${baseURL}/asset-bookings/${id}/approve/`;
    const config = { headers: { Authorization: `Bearer ${token}` } };
    const res = await axios.post(url, {}, config);
    return res.data;
};

export const rejectAssetBooking = async (id, admin_note = '') => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Not authenticated');

    const url = `${baseURL}/asset-bookings/${id}/reject/`;
    const config = { headers: { Authorization: `Bearer ${token}` } };
    const res = await axios.post(url, { admin_note }, config);
    return res.data;
};

// CONTACT FORM

export const sendContactMessage = async ({ name, email, nachricht, recaptcha_token }) => {
    const url = `${baseURL}/api/contact/`;
    try {
        const payload = { name, email, nachricht };
        if (recaptcha_token) payload.recaptcha_token = recaptcha_token;
        const res = await axios.post(url, payload);
        return res.data;
    } catch (error) {
        console.error('Error sending contact message:', error);
        throw error;
    }
};


export const getAllBlogEntries = async () => {
    const url = baseURL + '/blog/';
    const auth = {
        username: process.env.REACT_APP_API_USERNAME,
        password: process.env.REACT_APP_API_PASSWORD
    };
    const res = await axios.get(url, {auth});
    return res.data.results; // Zugriff auf das 'results'-Array
};

export const createEvent = async (eventData, token) => {
    const url = baseURL + '/events/';
    try {
        const res = await axios.post(url, eventData, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return res.data;
    } catch (error) {
        console.error("Error creating event:", error);
        throw error;
    }
};

export const updateEvent = async (eventId, updatedData, token) => {
    const url = `${baseURL}/events/${eventId}/`;
    try {
        const res = await axios.put(url, updatedData, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return res.data;
    } catch (error) {
        console.error("Error updating event:", error);
        throw error;
    }
};

export const deleteEvent = async (eventId, token) => {
    const url = `${baseURL}/events/${eventId}/`;
    try {
        const res = await axios.delete(url, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return res.status;
    } catch (error) {
        console.error("Error deleting event:", error);
        throw error;
    }
};


// Fetch all images (supports DRF pagination)
export const getAllImages = async () => {
    const token = localStorage.getItem('token');
    const config = token
        ? { headers: { Authorization: `Bearer ${token}` } }
        : {};
    try {
        const allImages = [];
        let nextUrl = `${baseURL}/images/`;

        let pageCount = 0;
        const MAX_PAGES = 100;

        while (nextUrl) {
            pageCount += 1;
            if (pageCount > MAX_PAGES) {
                throw new Error(`Pagination exceeded ${MAX_PAGES} pages for images endpoint`);
            }

            const response = await axios.get(nextUrl, config);
            const data = response.data;

            // Non-paginated response fallback
            if (Array.isArray(data)) return data;

            const results = data?.results;
            if (Array.isArray(results)) allImages.push(...results);

            // Django REST Framework typically returns absolute URLs in `next`
            nextUrl = toHttpsIfNeeded(data?.next) || null;
            // ...but support relative URLs as well
            if (nextUrl && typeof nextUrl === 'string' && nextUrl.startsWith('/')) {
                nextUrl = `${baseURL}${nextUrl}`;
            }
        }

        return allImages;
    } catch (error) {
        console.error("Error fetching gallery images:", error);
        throw error;
    }
};

// Fetch a random image from the gallery
export const getRandomImage = async () => {
    try {
        const images = await getAllImages(); // Reuse the updated getAllImages function
        const activeImages = images.filter(image => image.is_active); // Nur aktive Bilder filtern
        if (activeImages.length > 0) {
            const randomIndex = Math.floor(Math.random() * activeImages.length);
            return activeImages[randomIndex]; // Zufälliges aktives Bild zurückgeben
        }
        return null; // Keine aktiven Bilder verfügbar
    } catch (error) {
        console.error("Error fetching random image:", error);
        throw error;
    }
};

export const getAlbumById = async (albumId) => {
    const token = localStorage.getItem('token');
    const config = token
        ? { headers: { Authorization: `Bearer ${token}` } }
        : {};
    try {
        const response = await axios.get(`${baseURL}/albums/${albumId}/`, config);
        return response.data;
    } catch (error) {
        console.error("Error fetching album:", error);
        throw error;
    }
};

export const getAllAlbums = async () => {
    const token = localStorage.getItem('token');
    const config = token
        ? { headers: { Authorization: `Bearer ${token}` } }
        : {};
    try {
        const response = await axios.get(`${baseURL}/albums/`, config);
        if (Array.isArray(response.data)) return response.data;
        if (response.data && Array.isArray(response.data.results)) return response.data.results;
        return [];
    } catch (error) {
        console.error("Error fetching albums:", error);
        return [];
    }
};

export const getImagesByAlbum = async (albumId) => {
    const token = localStorage.getItem('token');
    const config = token
        ? { headers: { Authorization: `Bearer ${token}` } }
        : {};
    try {
        const allImages = [];
        let nextUrl = `${baseURL}/images/?album=${albumId}`;

        let pageCount = 0;
        const MAX_PAGES = 100;

        while (nextUrl) {
            pageCount += 1;
            if (pageCount > MAX_PAGES) {
                throw new Error(`Pagination exceeded ${MAX_PAGES} pages for images-by-album endpoint (album=${albumId})`);
            }

            const response = await axios.get(nextUrl, config);
            const data = response.data;

            // Non-paginated response fallback
            if (Array.isArray(data)) return data;

            const results = data?.results;
            if (Array.isArray(results)) allImages.push(...results);

            nextUrl = toHttpsIfNeeded(data?.next) || null;
            if (nextUrl && typeof nextUrl === 'string' && nextUrl.startsWith('/')) {
                nextUrl = `${baseURL}${nextUrl}`;
            }
        }

        return allImages;
    } catch (error) {
        console.error("Error fetching album images:", error);
        return [];
    }
};

export const getEventPDFs = async (eventId) => {
    const token = localStorage.getItem('token');
    const config = token
        ? { headers: { Authorization: `Bearer ${token}` } }
        : {};
    try {
        const url = `${baseURL}/eventpdfs/?event=${eventId}`;
        const res = await axios.get(url, config);
        if (Array.isArray(res.data)) return res.data;
        if (res.data && Array.isArray(res.data.results)) return res.data.results;
        return [];
    } catch (error) {
        console.error("Error fetching event PDFs:", error);
        return [];
    }
};

export const uploadEventPDF = async (eventId, file) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append("event", eventId);
    formData.append("file", file);
    try {
        const url = `${baseURL}/eventpdfs/`;
        const res = await axios.post(url, formData, {
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "multipart/form-data",
            },
        });
        return res.data;
    } catch (error) {
        console.error("Error uploading event PDF:", error);
        throw error;
    }
};

export const sendEventAnnouncement = async (eventId) => {
    const token = localStorage.getItem('token');
    const url = `${baseURL}/events/${eventId}/send-announcement/`;
    const res = await axios.post(url, {}, {
        headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
};

// AUTH

// Password reset by username/email
// Backend: path('api/password-reset/', views.password_reset_by_username, name='password_reset_by_username')
// Input JSON: { username }
export const passwordResetByUsername = async (username) => {
    const url = `${baseURL}/api/password-reset/`;
    try {
        const res = await axios.post(url, { username });
        return res.data;
    } catch (error) {
        console.error("Error requesting password reset:", error);
        throw error;
    }
};
